<script setup>
import { Head, Link } from '@inertiajs/vue3';
import UnauthLayout from "@/Layouts/UnauthLayout.vue";
import Footer from '@/Components/Footer.vue';
import { trans } from 'laravel-vue-i18n';
import { ref } from "vue";
import { usePage } from '@inertiajs/vue3';

const localizedPath = (path) => {
    const currentLocale = usePage().props.locale;
    return `/${currentLocale}${path}`;
};

defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
    laravelVersion: String,
    phpVersion: String,
});

const accordionItems = ref([
    {
        question: trans('public.welcome.faq.1.question'),
        answer: trans('public.welcome.faq.1.answer')
    },
    {
        question: trans('public.welcome.faq.2.question'),
        answer: trans('public.welcome.faq.2.answer')
    },
    {
        question: trans('public.welcome.faq.3.question'),
        answer: trans('public.welcome.faq.3.answer')
    },
    {
        question: trans('public.welcome.faq.4.question'),
        answer: trans('public.welcome.faq.4.answer')
    }
]);

const activeAccordion = ref(0);

function toggleAccordion(index) {
    activeAccordion.value = activeAccordion.value === index ? null : index;
}
</script>

<template>
    <UnauthLayout>
        <Head>
            <title>{{ trans('public.meta.title') }}</title>
            <meta
                name="description"
                :content="trans('public.meta.description')"
            />
            <link rel="icon" type="image/svg+xml" href="/images/logo-seeo2.svg" />
            <meta property="og:title" :content="trans('public.meta.title')" />
            <meta property="og:description" :content="trans('public.meta.description')" />
            <meta property="og:image" content="/images/og-image.jpg" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="SeeO2" />
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:title" :content="trans('public.meta.title')" />
            <meta property="twitter:description" :content="trans('public.meta.description')" />
            <meta property="twitter:image" content="/images/og-image.jpg" />
        </Head>
        <div class="relative home overflow-x-hidden">
            <!-- Intro Section -->
            <section class="min-h-screen flex items-start pt-14">   
                <div class="grid lg:grid-cols-6 grid-cols-1 w-full max-w-7xl gap-6 mx-auto relative lg:-translate-x-16 px-4 lg:px-8">
                   
                    <div class="lg:col-span-3 text-left z-10 order-2 lg:order-1">
                        <div class="lg:h-[600px]">
                            <h1 class="nume-produs">SeeO2</h1>
                            <span class="block text-4xl lg:text-6xl font-bold text-teal-700 mb-6">
                                {{ trans('public.welcome.hero.title') }}
                            </span>
                            <div class="absolute hidden lg:block -left-4 -top-4 w-20 h-20 bg-teal-100 rounded-full -z-10 opacity-50"></div>
                            <div class="flex flex-wrap gap-3 mb-8">
                                <Link
                                    href="#label"
                                    class="px-6 py-3 bg-teal-500 text-white rounded-full text-lg font-medium hover:bg-teal-600 transition"
                                >
                                    {{ trans('public.welcome.hero.cta1') }}
                                </Link>
                                <a
                                    :href="localizedPath('/book-meeting')"
                                    class="px-6 py-3 bg-white text-teal-500 border border-teal-500 rounded-full text-lg font-medium hover:bg-teal-50 transition"
                                >
                                    {{ trans('public.welcome.hero.cta2') }}
                                </a>
                            </div>
                            <div class="flex flex-col gap-4">
                                <div>
                                    <div
                                        v-for="(item, index) in accordionItems"
                                        :key="index"
                                        class="border-b py-2"
                                    >
                                        <button
                                            class="accordion-header w-full flex justify-between items-center"
                                            @click="toggleAccordion(index)"
                                        >
                                            <span class="text-left flex-1 pr-4 text-lg lg:text-2xl">{{ item.question }}</span>
                                            <span class="text-2xl flex-shrink-0">{{ activeAccordion === index ? '-' : '+' }}</span>
                                        </button>
                                        <div
                                            v-show="activeAccordion === index"
                                            class="accordion-content pt-2"
                                        >
                                            <p class="text-gray-600 text-base lg:text-lg">{{ item.answer }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                
                    <div class="lg:col-span-3 flex items-start order-1 lg:order-2 small-business">
                        <div class="w-full lg:w-[120%] lg:-mr-[20%] p-4 lg:p-3 bg-white lg:translate-x-12">
                            <img
                                src="/images/afacere-mica.jpg"
                                class="w-full h-[300px] lg:h-[625px] object-cover clip-corners"
                                alt="Business Image"
                            />
                        </div>
                        <div class="absolute hidden lg:block right-0 bottom-12 w-48 h-48 bg-[#D9F99D] rounded-full z-20 opacity-50"></div>
                    </div>
                </div>
            </section>
            
            <hr class="my-8 lg:my-12">

            <!-- About Section -->
            <section class="bg-teal-800 text-white py-12 lg:py-16 px-4 lg:px-6 mt-8 lg:-mt-12" id="about" style="background-color: #115E59; background-size: auto 100%; background-position: right bottom; background-repeat: no-repeat; background-image: url('data:image/svg+xml,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%221113%22%20height%3D%22695%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M1109.94 958.201c-1.31 1.266-3.84-1.352-5.15-.086-16.89 8.739-38.89 14.816-51.99 27.482-30.12 29.133-55.216 66.073-87.868 92.593-121.571 104.91-267.169 106.37-387.897 7.75-96.582-78.89-102.233-202.658-10.608-288.743 99.438-91.11 247.828-105.403 357.289-26.297 49.644 35.604 86.154 86.45 127.934 129.653 10.13 10.474 20.18 26.099 33.01 28.888 38.55 5.79 75.92 5.12 128.71 7.282-18.96-22.214-29.09-32.688-37.95-41.852-77.24-79.861-160.99-155.965-231.683-242.159-64.363-79.648-138.542-111.793-236.726-95.382-18.073 2.278-35.011 13.592-49.372 24.949-36.667 35.464-65.694 76.208-106.159 107.745-114.894 90.854-261.671 85.85-372.139-10.02-91.431-78.807-97.255-192.27-13.358-278.483 95.683-97.613 246.735-117.015 358.773-37.867 49.644 35.604 89.991 87.802 133.044 132.314 10.13 10.474 20.174 26.099 33.053 26.312 38.55 5.791 77.231 3.854 118.4 7.111-1.093-11.612.26-15.454-1.006-16.764-93.704-96.88-184.832-193.718-279.845-289.332-8.864-9.165-30.78-8.239-46.235-8.495-73.389-2.502-149.57 7.833-220.21-4.93C110.418 181.598 8.24 52.371.532-102.347c-6.57-145.681 85.184-239.494 232.005-237.066 153.238 3.823 289.292 110.422 315.072 262.862 12.934 73.645 1.29 153.324-.008 230.597-.26 15.455-4.448 34.71 4.416 43.874 93.66 99.457 188.63 197.646 283.6 295.836 6.461-1.181 11.656-3.672 18.117-4.853-2.186-23.225-6.948-46.492-5.249-70.941 3.225-38.594-5.336-65.789-41.008-89.568-38.205-26.397-71.084-63.012-97.503-100.808-76.809-105.619-78.791-217.73-9.959-327.381 67.522-108.385 186.571-139.91 274.376-75.334 52.179 38.223 87.369 90.335 106.939 152.495 38.88 139.774-17.07 249.639-125.29 326.432-66.527 49.142-84.157 101.669-75.065 173.961 3.409 27.11 6.689 61.947 24.417 80.276 98.678 107.269 202.558 212.047 305.128 318.093 7.6 7.855 17.77 15.753 38.12 31.548-1.97-36.104-4.12-61.904-1.15-85.044 5.8-38.551-8-60.68-39.79-85.682-40.73-29.016-76.15-68.249-105.1-108.664-75.5-106.886-72.15-229.214.39-329.787 93.89-144.019 256.03-133.608 339.75 21.079 74.98 137.795 44.18 284.147-101.97 395.096-58.71 44.117-78.79 88.874-72.19 155.973 1.18 6.46-.21 12.879-.3 18.03-5.76 35.976 9.26 61.99 34.59 88.174 57.03 56.339 112.7 116.519 169.68 175.429l-35.36 34.2c-10.04-15.62-17.51-31.21-27.64-41.68-80.87-94.09-145.85-213.686-299.61-186.599Z%22%20fill%3D%22%2347B27A%22%2F%3E%3C%2Fsvg%3E');">
                <div class="max-w-7xl mx-auto grid lg:grid-cols-7 grid-cols-1 items-center gap-8 lg:gap-12 lg:-translate-x-12">
                    <div class="lg:col-span-3">
                        <h2 class="text-4xl lg:text-7xl font-bold mb-4 lg:mb-6">{{ trans('public.welcome.about.title') }}</h2>
                        <p class="text-xl lg:text-2xl font-light mb-6 lg:mb-8 leading-relaxed">
                            {{ trans('public.welcome.about.description') }}
                        </p>
                        <p class="text-lg font-light mb-4">
                            {{ trans('public.welcome.about.description2') }}
                        </p>
                    </div>
                    <div class="lg:col-span-4 flex justify-end imagine-cu-chenar">
                        <img 
                            src="/images/seeo2-platforma.jpg" 
                            alt="seeo2"
                            class="w-[95%] shadow-xl ml-auto"
                        >
                    </div>
                </div>
            </section>
            <section id="label" class="py-16 lg:py-24">
                <div class="max-w-7xl mx-auto px-4 lg:px-8">
                    <div class="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div class="order-2 lg:order-1">
                            <img 
                                src="/images/eticheta-ne-pasa.png" 
                                alt="Eticheta Ne Pasa"
                                class="w-full max-w-md mx-auto"
                            >
                        </div>
                        <div class="order-1 lg:order-2 relative">
                            <h3 class="text-4xl lg:text-6xl font-bold text-teal-700 mb-6">{{ trans('public.welcome.label.title') }}</h3>
                            <p class="text-lg text-gray-600 leading-relaxed">
                                {{ trans('public.welcome.label.description') }}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section id="ghg-protocol" class="bg-gray-50 py-16 lg:py-32 px-6">
                <div class="max-w-7xl mx-auto">
                    <div class="text-center mb-12">
                        <h3 class="text-4xl lg:text-7xl font-bold text-teal-700 mb-6">{{ trans('public.welcome.ghg.title') }}</h3>
                        <p class="text-lg font-light text-gray-600 mt-4 max-w-4xl mx-auto px-4">
                            {{ trans('public.welcome.ghg.description') }}
                        </p>
                    </div>
                </div>
            </section>
            <!-- Greenwashing Section -->
            <section class="relative min-h-[600px] flex items-center pt-[300px] lg:pt-0" id="greenwashing">
                <div class="absolute inset-0">
                    <img 
                        src="/images/fundal-greenwashing.jpg" 
                        alt="background"
                        class="w-full h-full object-cover"
                    >
                </div>
                <div class="max-w-7xl mx-auto w-full relative z-20">
                    <div class="grid lg:grid-cols-2 grid-cols-1 gap-8 items-center">
                        <div class="p-4 lg:p-3 lg:-translate-x-12">
                            
                        </div>
                        <div class="ml-auto max-w-xl pr-8 relative">
                            <div class="absolute inset-0 bg-[rgba(73,87,81,0.6)] backdrop-blur-[35px]"></div>
                            <div class="relative z-10 p-12">
                                <h2 class="text-4xl lg:text-6xl font-bold text-white mb-6">{{ trans('public.welcome.greenwashing.title') }}</h2>
                                <p class="text-xl font-light text-gray-200 mb-8">
                                    {{ trans('public.welcome.greenwashing.description') }}
                                </p>
                                <a
                                    href="https://www.researchgate.net/publication/345844645_Greenwashing_behaviours_Causes_taxonomy_and_consequences_based_on_a_systematic_literature_review"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="inline-block px-6 py-3 bg-green-400 text-gray-900 rounded-full text-lg font-medium hover:bg-green-300 transition"
                                >
                                    {{ trans('public.welcome.greenwashing.learn_more') }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            <!-- Accordion Section -->
            <section class="max-w-7xl mx-auto">
                
            </section>
        </div>
        <Footer />
    </UnauthLayout>
</template>

<style scoped>
.circle-bg::before {
    content: '';
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background-color: #F0FDFA;
    top: -100px;
    right: -100px;
    z-index: -1;
}

.home {
    overflow-x: hidden;
}
</style>
